import { DestroyRef, inject, Injectable } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Store } from "@ngrx/store";
import { EventTypes, PublicEventsService } from "angular-auth-oidc-client";
import { filter, switchMap, take } from "rxjs/operators";

import { LogoutRequested } from "../../store/actions/user.actions";

/**
 * Reacts to the OIDC library's confirmed-failure event so the app pushes
 * the user to re-authenticate instead of silently sending expired tokens
 * (closes one of the four gaps from the 2026-04-28 401-recovery
 * investigation). Wiring is deliberately narrow:
 *
 * <ul>
 *   <li><b>Gates on {@code CheckingAuthFinished}</b>. The library's
 *       {@code PublicEventsService.notify} is a {@code ReplaySubject(1)},
 *       so a fresh subscriber receives the last fired event immediately —
 *       which during bootstrap is often {@code TokenExpired} from a
 *       routine validation pass. Waiting for {@code CheckingAuthFinished}
 *       (one-shot) before opening the failure subscription avoids
 *       reacting to that startup replay.</li>
 *   <li><b>Listens only to {@code SilentRenewFailed}</b>. {@code
 *       TokenExpired} is a normal-flow validation event, not a failure;
 *       the library handles it via its own periodic renew loop.
 *       {@code SilentRenewFailed} is the signal that the library tried
 *       and gave up.</li>
 *   <li><b>Dispatches {@code LogoutRequested}</b>. By the time
 *       {@code SilentRenewFailed} fires, calling
 *       {@code forceRefreshSession()} would race with whatever the
 *       library is doing. Pushing logout cleanly resets state and
 *       redirects the user to Cognito.</li>
 * </ul>
 */
@Injectable({ providedIn: "root" })
export class AuthEventsService {
	private readonly publicEvents = inject(PublicEventsService);
	private readonly store = inject(Store);
	private readonly destroyRef = inject(DestroyRef);

	private started = false;

	/** Idempotent — safe to call multiple times. */
	start(): void {
		if (this.started) {
			return;
		}
		this.started = true;
		this.publicEvents
			.registerForEvents()
			.pipe(
				filter((event) => event.type === EventTypes.CheckingAuthFinished),
				take(1),
				switchMap(() =>
					this.publicEvents
						.registerForEvents()
						.pipe(filter((event) => event.type === EventTypes.SilentRenewFailed))
				),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe(() => this.store.dispatch(LogoutRequested()));
	}
}
